/**
 * 文案生成输入表单
 * Step 1：参考文案、笔记类型、背景信息、特殊要求、生成数量
 */

import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import {
  Sparkles,
  Loader2,
  Search,
  CheckCircle2,
  Info,
  ArrowRight,
  Eye,
} from 'lucide-react'
import type { Project } from '@/types'
import type { CopywritingFormCache, ReferenceAnalysis } from '@/hooks/useCopywriting'
import { useNoteStyles } from '@/hooks/useNoteStyles'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Textarea } from '@/components/ui/textarea'
import { Slider } from '@/components/ui/slider'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'

interface CopywritingInputFormProps {
  project: Project
  isGenerating: boolean
  isAnalyzing: boolean
  error: string | null
  referenceAnalysis: ReferenceAnalysis | null
  onAnalyzeReference: (text: string) => Promise<ReferenceAnalysis>
  onSubmit: (data: CopywritingFormCache) => void
  loadFormCache: () => CopywritingFormCache | null
  saveFormCache: (data: CopywritingFormCache) => void
  hasResults: boolean
  onViewResults: () => void
}

// 表单验证 Schema
const formSchema = z.object({
  referenceText: z.string().optional(),
  copywritingStyleIds: z.array(z.string()).min(1, '请至少选择一个文案风格'),
  background: z.string().min(1, '请输入笔记背景信息'),
  requirements: z.string().optional(),
  count: z.number().min(1).max(40),
})

type FormData = z.infer<typeof formSchema>

export function CopywritingInputForm({
  project,
  isGenerating,
  isAnalyzing,
  error,
  referenceAnalysis,
  onAnalyzeReference,
  onSubmit,
  loadFormCache,
  saveFormCache,
  hasResults,
  onViewResults,
}: CopywritingInputFormProps) {
  const [analyzeError, setAnalyzeError] = useState<string | null>(null)
  const { copywritingStyles, copywritingStyleMap } = useNoteStyles()

  // 获取可用的文案风格列表（优先使用项目关联的风格，否则显示所有风格）
  const projectStyleId = project.note_style_id
  const projectStyles = projectStyleId ? (copywritingStyleMap[projectStyleId] || []) : []
  const availableCopywritingStyles = projectStyles.length > 0 ? projectStyles : copywritingStyles
  const defaultStyleId = availableCopywritingStyles[0]?.id || ''

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      referenceText: '',
      copywritingStyleIds: defaultStyleId ? [defaultStyleId] : [],
      background: project.title || project.description || '',
      requirements: '',
      count: 3,
    },
  })

  // 加载缓存的表单数据
  // 使用 JSON.stringify 避免数组引用变化导致的无限循环
  const availableStyleIdsKey = JSON.stringify(availableCopywritingStyles.map(s => s.id))
  
  useEffect(() => {
    const cached = loadFormCache()
    if (cached) {
      // 过滤掉不存在的文案风格 ID
      const validStyleIds = (cached.copywritingStyleIds || []).filter((id: string) =>
        availableCopywritingStyles.some(s => s.id === id)
      )
      
      // 只有当缓存值与当前表单值不同时才重置，避免不必要的更新
      const currentValues = form.getValues()
      const newValues = {
        referenceText: cached.referenceText || '',
        copywritingStyleIds: validStyleIds.length > 0 ? validStyleIds : (defaultStyleId ? [defaultStyleId] : []),
        background: cached.background,
        requirements: cached.requirements || '',
        count: cached.count,
      }

      // 简单比较是否需要重置 (这里主要防止无限循环)
      if (JSON.stringify(currentValues) !== JSON.stringify(newValues)) {
         form.reset(newValues)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadFormCache, form, defaultStyleId, availableStyleIdsKey])

  // 分析参考文案
  const handleAnalyzeReference = async () => {
    const text = form.getValues('referenceText')
    if (!text || text.trim().length < 10) {
      setAnalyzeError('请输入至少 10 个字符的参考文案')
      return
    }

    setAnalyzeError(null)
    try {
      await onAnalyzeReference(text)
    } catch (err) {
      setAnalyzeError(err instanceof Error ? err.message : '分析失败')
    }
  }

  // 表单提交
  const handleSubmit = (data: FormData) => {
    const cacheData: CopywritingFormCache = {
      referenceText: data.referenceText || '',
      copywritingStyleIds: data.copywritingStyleIds,
      background: data.background,
      requirements: data.requirements || '',
      count: data.count,
    }
    saveFormCache(cacheData)
    onSubmit(cacheData)
  }

  return (
    <div className="space-y-6">
      {/* 参考文案区域 */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="w-5 h-5 text-primary" />
            参考文案（可选）
          </CardTitle>
          <CardDescription>
            粘贴优秀的小红书文案，AI 将分析其风格特点辅助生成
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Textarea
            placeholder="粘贴参考文案...（后续将接入爆款文案检索）"
            className="min-h-[120px] resize-none"
            {...form.register('referenceText')}
          />

          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAnalyzeReference}
              disabled={isAnalyzing}
            >
              {isAnalyzing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  分析中...
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" />
                  分析风格
                </>
              )}
            </Button>

            {analyzeError && (
              <span className="text-sm text-destructive">{analyzeError}</span>
            )}
          </div>

          {/* 分析结果展示 - 精简版 */}
          {referenceAnalysis && referenceAnalysis.meta_analysis && (
            <div className="p-4 bg-muted/50 rounded-lg space-y-3 animate-in fade-in slide-in-from-top-2">
              {/* 头部：分析完成标识 + 核心指标 */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-medium text-green-600">
                  <CheckCircle2 className="w-4 h-4" />
                  分析完成
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">
                    {referenceAnalysis.meta_analysis.category}
                  </Badge>
                  <Badge variant="default" className="text-xs">
                    {referenceAnalysis.meta_analysis.score}
                  </Badge>
                </div>
              </div>

              {/* 核心信息：单行展示 */}
              <div className="grid grid-cols-2 gap-2 text-xs">
                {referenceAnalysis.hook_strategy && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-muted-foreground">标题：</span>
                    <span className="font-medium">{referenceAnalysis.hook_strategy.title_type}</span>
                  </div>
                )}
                {referenceAnalysis.style_and_structure && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-muted-foreground">语气：</span>
                    <span className="font-medium truncate">{referenceAnalysis.style_and_structure.tone}</span>
                  </div>
                )}
                {referenceAnalysis.word_count && (
                  <>
                    <div className="flex items-center gap-1.5">
                      <span className="text-muted-foreground">总字数：</span>
                      <span className="font-mono font-medium">{referenceAnalysis.word_count.total}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-muted-foreground">标题长度：</span>
                      <span className="font-mono font-medium">{referenceAnalysis.word_count.title_length}</span>
                    </div>
                  </>
                )}
              </div>

              {/* 核心词和建议 */}
              {referenceAnalysis.hook_strategy?.keywords && referenceAnalysis.hook_strategy.keywords.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {referenceAnalysis.hook_strategy.keywords.slice(0, 5).map((k: string, i: number) => (
                    <Badge key={i} variant="secondary" className="text-xs">
                      {k}
                    </Badge>
                  ))}
                </div>
              )}

              {/* 仿写建议 - 仅显示第一条 */}
              {referenceAnalysis.replication_suggestions?.[0] && (
                <div className="text-xs text-muted-foreground border-l-2 border-primary/30 pl-3 py-1">
                  💡 {referenceAnalysis.replication_suggestions[0]}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* 生成配置表单 */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            生成配置
          </CardTitle>
          <CardDescription>
            设置笔记类型、风格和生成参数
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
              {/* 文案风格（多选） */}
              <FormField
                control={form.control}
                name="copywritingStyleIds"
                render={() => (
                  <FormItem>
                    <FormLabel>文案风格（可多选）</FormLabel>
                    {availableCopywritingStyles.length === 0 ? (
                      <div className="border border-dashed rounded-lg p-4 bg-muted/30 text-sm text-muted-foreground">
                        当前风格暂无可用的文案风格，请先在「笔记风格」页面配置
                      </div>
                    ) : (
                      <div className="space-y-3 border rounded-lg p-4 bg-muted/30">
                        {availableCopywritingStyles.map(style => (
                          <FormField
                            key={style.id}
                            control={form.control}
                            name="copywritingStyleIds"
                            render={({ field }) => (
                              <FormItem className="flex items-start space-x-3 space-y-0">
                                <FormControl>
                                  <Checkbox
                                    checked={field.value?.includes(style.id)}
                                    onCheckedChange={(checked) => {
                                      const currentValue = field.value || []
                                      if (checked) {
                                        field.onChange([...currentValue, style.id])
                                      } else {
                                        field.onChange(
                                          currentValue.filter((val) => val !== style.id)
                                        )
                                      }
                                    }}
                                  />
                                </FormControl>
                                <div className="flex-1">
                                  <FormLabel className="font-normal cursor-pointer">
                                    <span className="font-medium">{style.name}</span>
                                    <span className="text-muted-foreground ml-2">- {style.tone}</span>
                                    <span className="text-xs text-muted-foreground ml-2">
                                      ({style.char_min}-{style.char_max}字, {style.hashtag_count}标签)
                                    </span>
                                  </FormLabel>
                                </div>
                              </FormItem>
                            )}
                          />
                        ))}
                      </div>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* 笔记背景信息 */}
              <FormField
                control={form.control}
                name="background"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>笔记背景信息 <span className="text-destructive">*</span></FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="描述你想要创作的笔记主题、场景、产品等信息...（必填）"
                        className="min-h-[80px] resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      详细的背景信息有助于生成更贴合需求的文案
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* 特殊要求 */}
              <FormField
                control={form.control}
                name="requirements"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>特殊要求（可选）</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="例如：需要包含特定关键词、避免某些表达、特定格式要求..."
                        className="min-h-[60px] resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* 生成数量 */}
              <FormField
                control={form.control}
                name="count"
                render={({ field }) => {
                  return (
                    <FormItem>
                      <FormLabel>生成数量：{field.value} 篇</FormLabel>
                      <FormControl>
                        <Slider
                          min={1}
                          max={40}
                          step={1}
                          value={[field.value]}
                          onValueChange={(vals) => field.onChange(vals[0])}
                          className="py-4"
                        />
                      </FormControl>
                      <FormDescription className="flex items-center gap-1">
                        <Info className="w-3 h-3" />
                        最多支持一次生成 40 篇文案，将分批生成
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )
                }}
              />

              {/* 错误提示 */}
              {error && (
                <div className="p-3 bg-destructive/10 text-destructive text-sm rounded-lg">
                  {error}
                </div>
              )}

              {/* 操作按钮 */}
              <div className="flex gap-3">
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={isGenerating || availableCopywritingStyles.length === 0}
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      生成中...
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-4 w-4" />
                      开始生成
                    </>
                  )}
                </Button>

                {hasResults && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onViewResults}
                    disabled={isGenerating}
                  >
                    <Eye className="mr-2 h-4 w-4" />
                    查看结果
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                )}
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  )
}


